import React, { useCallback, useEffect, useState } from 'react';
import {
  notifMeta, listChannels, createChannel, updateChannel, deleteChannel, testChannel,
  getNotifPrefs, saveNotifPrefs, notifLog,
} from '../api/modulesApi';
import { errorMessage } from '../api/securityApi';
import ModuleNav from '../components/Modules/ModuleNav';
import RequirePermission from '../auth/RequirePermission';

const blank = { type: 'Email', name: '', target: '', minSeverity: 'Medium', isEnabled: true };
const HELP = {
  Email: 'Address that receives alerts and digests.',
  Slack: 'Slack incoming-webhook URL (Slack → Apps → Incoming Webhooks).',
  Teams: 'Microsoft Teams incoming-webhook URL (Workflows or connector).',
  Discord: 'Discord channel webhook URL (Channel → Integrations → Webhooks).',
  Webhook: 'Any https endpoint. Receives a JSON POST.',
};

export default function NotificationsPage() {
  const [meta, setMeta] = useState({ types: [], severities: [], smtpConfigured: true });
  const [channels, setChannels] = useState([]);
  const [prefs, setPrefs] = useState(null);
  const [log, setLog] = useState([]);
  const [form, setForm] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const load = useCallback(async () => {
    try {
      const [m, c, p, l] = await Promise.all([notifMeta(), listChannels(), getNotifPrefs(), notifLog()]);
      setMeta(m); setChannels(c); setPrefs(p); setLog(l);
    } catch (e) { setError(errorMessage(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, ok) => {
    setError(''); setInfo('');
    try { await fn(); if (ok) setInfo(ok); await load(); } catch (e) { setError(errorMessage(e)); }
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
  const setP = (k) => (e) => setPrefs({ ...prefs, [k]: e.target.type === 'checkbox' ? e.target.checked : Number(e.target.value) });

  const save = (e) => {
    e.preventDefault();
    act(async () => {
      if (editingId) await updateChannel(editingId, form); else await createChannel(form);
      setForm(null); setEditingId(null);
    }, 'Channel saved.');
  };
  const test = async (id) => {
    setError(''); setInfo('');
    try { const r = await testChannel(id); r.ok ? setInfo('Test message sent.') : setError('Test failed: ' + r.message); await load(); }
    catch (e) { setError(errorMessage(e)); }
  };

  return (
    <div className="shell">
      <header className="page-header"><div><h1>Notifications</h1>
        <p className="muted">Choose where alerts and digests are delivered. Alerts themselves are defined under Security → Alerts.</p></div></header>
      <ModuleNav />
      {error && <div className="toast error">{error}</div>}
      {info && <div className="toast info">{info}</div>}
      {!meta.smtpConfigured && <div className="sec-warn">E-mail is not configured on the server (Smtp settings). E-mail channels, digests and scheduled reports will fail until it is.</div>}

      <div className="mod-two">
        <div className="sec-card sec-scroll mod-wide">
          <div className="sec-row" style={{ justifyContent: 'space-between' }}>
            <h3>Channels</h3>
            <RequirePermission code="workspace.write">
              {!form && <button className="primary" onClick={() => { setEditingId(null); setForm({ ...blank }); }}>+ Add channel</button>}
            </RequirePermission>
          </div>

          {form && (
            <form className="mod-form" onSubmit={save} style={{ margin: '12px 0 18px' }}>
              <label>Type
                <select className="sec-select" value={form.type} onChange={set('type')}>{meta.types.map((t) => <option key={t}>{t}</option>)}</select></label>
              <label>Name<input className="sec-input" required maxLength={100} value={form.name} onChange={set('name')} placeholder="e.g. Team Slack" /></label>
              <label>{form.type === 'Email' ? 'E-mail address' : 'Webhook URL'}
                <input className="sec-input" required value={form.target} onChange={set('target')} placeholder={form.type === 'Email' ? 'you@example.com' : 'https://…'} />
                <span className="mod-note">{HELP[form.type]}</span></label>
              <label>Only send alerts of at least this severity
                <select className="sec-select" value={form.minSeverity} onChange={set('minSeverity')}>{meta.severities.map((s) => <option key={s}>{s}</option>)}</select></label>
              <label style={{ display: 'block' }}><input type="checkbox" checked={form.isEnabled} onChange={set('isEnabled')} /> Enabled</label>
              <div className="sec-row">
                <button className="primary" type="submit">Save channel</button>
                <button className="ghost" type="button" onClick={() => { setForm(null); setEditingId(null); }}>Cancel</button>
              </div>
            </form>
          )}

          <table className="sec-table">
            <thead><tr><th>Name</th><th>Type</th><th>Target</th><th>Min severity</th><th>Last result</th><th>On</th><th /></tr></thead>
            <tbody>
              {channels.map((c) => (
                <tr key={c.id}>
                  <td><b>{c.name}</b></td><td>{c.type}</td><td className="sec-mono">{c.targetPreview}</td><td>{c.minSeverity}</td>
                  <td>{c.lastStatus || '—'}{c.lastSentAt ? <div className="muted">{new Date(c.lastSentAt).toLocaleString()}</div> : null}</td>
                  <td>{c.isEnabled ? 'Yes' : 'No'}</td>
                  <td><RequirePermission code="workspace.write"><div className="sec-row">
                    <button className="ghost" onClick={() => test(c.id)}>Send test</button>
                    <button className="ghost" onClick={() => { setEditingId(c.id); setForm({ type: c.type, name: c.name, target: '', minSeverity: c.minSeverity, isEnabled: c.isEnabled }); }}>Edit</button>
                    <button className="ghost danger" onClick={() => window.confirm('Delete this channel?') && act(() => deleteChannel(c.id))}>Delete</button>
                  </div></RequirePermission></td>
                </tr>
              ))}
              {channels.length === 0 && <tr><td colSpan="7" className="muted">No channels yet. Add one to receive alerts outside the app.</td></tr>}
            </tbody>
          </table>
          {editingId && <p className="mod-note">For safety the saved URL is hidden. Enter the target again when editing.</p>}
        </div>

        {prefs && (
          <div className="sec-card">
            <h3>Preferences</h3>
            <div className="mod-form">
              <label style={{ display: 'block' }}><input type="checkbox" checked={prefs.inAppEnabled} onChange={setP('inAppEnabled')} /> Show alerts in the app</label>
              <label style={{ display: 'block' }}><input type="checkbox" checked={prefs.dailyDigest} onChange={setP('dailyDigest')} /> Daily digest</label>
              <label style={{ display: 'block' }}><input type="checkbox" checked={prefs.weeklyDigest} onChange={setP('weeklyDigest')} /> Weekly digest (Mondays)</label>
              <label>Send digests after (UTC hour)<input className="sec-input" type="number" min="0" max="23" value={prefs.digestHourUtc} onChange={setP('digestHourUtc')} /></label>
              <label>Quiet hours start (UTC, -1 = off)<input className="sec-input" type="number" min="-1" max="23" value={prefs.quietStartHourUtc} onChange={setP('quietStartHourUtc')} /></label>
              <label>Quiet hours end (UTC)<input className="sec-input" type="number" min="-1" max="23" value={prefs.quietEndHourUtc} onChange={setP('quietEndHourUtc')} /></label>
              <p className="mod-note">During quiet hours alerts are still recorded in the inbox but not pushed to channels.</p>
              <RequirePermission code="workspace.write">
                <button className="primary" onClick={() => act(() => saveNotifPrefs(prefs), 'Preferences saved.')}>Save preferences</button>
              </RequirePermission>
            </div>
          </div>
        )}

        <div className="sec-card sec-scroll">
          <h3>Recent deliveries</h3>
          <table className="sec-table">
            <thead><tr><th>When</th><th>Channel</th><th>Subject</th><th>Result</th></tr></thead>
            <tbody>
              {log.slice(0, 25).map((l) => (
                <tr key={l.id}><td>{new Date(l.createdAt).toLocaleString()}</td><td>{l.channelType}</td><td>{l.subject}</td>
                  <td style={{ color: l.success ? '#047857' : '#b91c1c' }}>{l.success ? 'Sent' : l.error}</td></tr>
              ))}
              {log.length === 0 && <tr><td colSpan="4" className="muted">Nothing sent yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
